import React, { useEffect, useRef, useState } from "react";

import { MensajeEncontrado } from "../../../services/v2/prospects.service";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { cuando, iniciales, nombreCompleto, tonoAvatar } from "./salesHelpers";

/**
 * Buscar mensajes como WhatsApp (Esteban, 2026-10-12: "no se pueden buscar mensajes en los chats... muy importante"):
 * - En la lista de WappChat, debajo de las personas, las conversaciones que dicen la palabra (con el pedacito).
 * - Dentro de una conversación (WappChat y WappTeam), la lupa del encabezado busca en TODO el historial en el servidor,
 *   enseña los resultados con su fecha y al tocar uno lleva al mensaje y lo resalta; ↑ ↓ para pasar de uno a otro.
 * Se pide al servidor 350 ms después de dejar de teclear y desde 2-3 letras; una respuesta vieja nunca pisa a la nueva.
 */

export const sinAcentos = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** El texto con las palabras buscadas resaltadas (sin importar mayúsculas ni acentos). */
export const Resaltado: React.FC<{ texto: string; q: string }> = ({ texto, q }) => {
	const palabras = q.trim().split(/\s+/).filter((w) => w.length >= 2).map(sinAcentos);
	if (!palabras.length || !texto) return <>{texto}</>;
	// Se busca sobre la versión sin acentos (mismo largo letra por letra) y se corta el texto original en esos lugares.
	const plano = sinAcentosMismoLargo(texto);
	const marcas: [number, number][] = [];
	palabras.forEach((w) => {
		let i = plano.indexOf(w);
		while (i >= 0 && marcas.length < 30) { marcas.push([i, i + w.length]); i = plano.indexOf(w, i + w.length); }
	});
	if (!marcas.length) return <>{texto}</>;
	marcas.sort((a, b) => a[0] - b[0]);
	const out: React.ReactNode[] = [];
	let pos = 0;
	marcas.forEach(([a, b], k) => {
		if (a < pos) return;
		if (a > pos) out.push(texto.slice(pos, a));
		out.push(<mark key={k} className="bm-mark">{texto.slice(a, b)}</mark>);
		pos = b;
	});
	if (pos < texto.length) out.push(texto.slice(pos));
	return <>{out}</>;
};

/** Minúsculas sin acentos, letra por letra (para que las posiciones coincidan con el texto original). */
const sinAcentosMismoLargo = (s: string) => Array.from(s).map((c) => {
	const x = sinAcentos(c);
	if (x.length === c.length) return x;
	const l = c.toLowerCase();
	return l.length === c.length ? l : c;
}).join("");

/**
 * Pide resultados mientras se escribe: espera `ms` sin teclear, desde `min` letras, y descarta respuestas viejas.
 * `llave` cambia cuando cambia el contexto (otra persona, otro filtro): vuelve a buscar con el mismo texto.
 */
export function useBuscarDiferido<T>(texto: string, min: number, llave: string, pedir: (q: string) => Promise<T[]>, ms = 350) {
	const [resultados, setResultados] = useState<T[] | null>(null);
	const [buscando, setBuscando] = useState<boolean>(false);
	const turno = useRef<number>(0);
	const pedirRef = useRef(pedir);
	pedirRef.current = pedir;
	useEffect(() => {
		const q = texto.trim();
		const yo = ++turno.current;
		if (q.length < min) { setResultados(null); setBuscando(false); return; }
		setBuscando(true);
		const t = setTimeout(() => {
			pedirRef.current(q).then((r) => { if (yo === turno.current) setResultados(r); })
				.catch(() => { if (yo === turno.current) setResultados([]); })
				.finally(() => { if (yo === turno.current) setBuscando(false); });
		}, ms);
		return () => clearTimeout(t);
	}, [texto, min, llave]);
	return { resultados, buscando };
}

/** Debajo de la lista de personas: "Mensajes" que dicen lo buscado, una fila por persona. */
export const MensajesEncontrados: React.FC<{ q: string; llave: string; pedir: (q: string) => Promise<MensajeEncontrado[]>; activo: number | null;
	onAbrir: (r: MensajeEncontrado) => void }> = ({ q, llave, pedir, activo, onAbrir }) => {
	// q ya llega con espera (la lista la aplica al dejar de teclear): aquí se pide de inmediato.
	const { resultados, buscando } = useBuscarDiferido<MensajeEncontrado>(q, 3, llave, pedir, 0);
	if (q.trim().length < 3) return null;
	return (
		<div className="bm-lista-msgs">
			<div className="bm-titulo">Mensajes{buscando ? <span className="wl-cargando" aria-label="Buscando" /> : null}</div>
			{resultados && !resultados.length && !buscando ? <div className="bm-vacio">Ninguna conversación dice «{q.trim()}».</div> : null}
			{(resultados || []).map((r) => {
				const nombre = nombreCompleto(r as any).trim() || r.wa_nombre || (r.phone_e164 ? telefonoBonito(r.phone_e164) : "Sin nombre");
				return (
					<button key={r.prospect_id + "-" + r.event_id} className={"ventas-fila bm-fila" + (activo === r.prospect_id ? " activa" : "")} onClick={() => onAbrir(r)}>
						<span className={"av " + tonoAvatar(r.prospect_id)} aria-hidden="true">{iniciales(r as any)}</span>
						<span className="cuerpo">
							<span className="l1"><span className="nombre">{nombre}</span><span className="cuando">{cuando(r.occurred_at)}</span></span>
							<span className="l2"><span className="txt bm-txt">{r.direction === "out" ? "Tú: " : ""}<Resaltado texto={r.pedacito} q={q} /></span>
								{r.n > 1 ? <span className="bm-n" title={r.n + " mensajes coinciden"}>{r.n}</span> : null}</span>
						</span>
					</button>
				);
			})}
		</div>
	);
};

export interface ItemBusqueda { id: number; fecha: string; quien: string; texto: string }

/**
 * La lupa dentro de una conversación: caja de búsqueda, lista de resultados (lo más nuevo arriba) y ↑ ↓ para recorrerlos.
 * Tocar un resultado llama a onIr(id); la lista se esconde para ver el mensaje y vuelve al escribir o con "Resultados".
 */
export const BuscadorChat: React.FC<{ llave: string; pedir: (q: string) => Promise<ItemBusqueda[]>; onIr: (id: number) => void; onCerrar: () => void; min?: number }> = ({ llave, pedir, onIr, onCerrar, min = 2 }) => {
	const [texto, setTexto] = useState<string>("");
	const [actual, setActual] = useState<number>(-1);
	const [verLista, setVerLista] = useState<boolean>(true);
	const { resultados, buscando } = useBuscarDiferido<ItemBusqueda>(texto, min, llave, pedir);
	// Resultados nuevos: se vuelve a la lista y a ninguno elegido.
	useEffect(() => { setActual(-1); setVerLista(true); }, [resultados]);
	const ir = (i: number) => {
		if (!resultados || i < 0 || i >= resultados.length) return;
		setActual(i); setVerLista(false); onIr(resultados[i].id);
	};
	const n = resultados?.length || 0;
	return (
		<div className="bm-chat" role="search">
			<div className="bm-barra">
				<svg className="bm-lupa" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" /><path d="M15.5 15.5 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
				<input type="search" autoFocus enterKeyHint="search" placeholder="Buscar en esta conversación" value={texto} aria-label="Buscar en esta conversación"
					onChange={(e) => setTexto(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Escape") onCerrar();
						if (e.key === "Enter") { e.preventDefault(); ir(actual < 0 ? 0 : Math.min(actual + 1, n - 1)); }
					}} />
				{n ? <span className="bm-cuenta">{actual >= 0 ? actual + 1 + " de " + n : n + (n === 1 ? " resultado" : " resultados")}</span> : null}
				{n ? <button className="bm-ico" disabled={actual >= n - 1} onClick={() => ir(actual + 1)} aria-label="Anterior (más viejo)" title="Anterior">
					<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 15l6-6 6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg></button> : null}
				{n ? <button className="bm-ico" disabled={actual <= 0} onClick={() => ir(actual - 1)} aria-label="Siguiente (más nuevo)" title="Siguiente">
					<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg></button> : null}
				{n && !verLista ? <button className="bm-ico" onClick={() => setVerLista(true)} aria-label="Ver resultados" title="Ver resultados">
					<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg></button> : null}
				<button className="bm-ico" onClick={onCerrar} aria-label="Cerrar búsqueda" title="Cerrar">
					<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg></button>
			</div>
			{verLista && texto.trim().length >= min ? (
				<div className="bm-resultados">
					{buscando && !resultados ? <div className="bm-vacio">Buscando…</div> : null}
					{resultados && !n && !buscando ? <div className="bm-vacio">No hay mensajes con «{texto.trim()}».</div> : null}
					{(resultados || []).map((r, i) => (
						<button key={r.id} className={"bm-item" + (i === actual ? " on" : "")} onClick={() => ir(i)}>
							<span className="bm-item-cab"><b>{r.quien}</b><span>{cuando(r.fecha)}</span></span>
							<span className="bm-item-txt"><Resaltado texto={r.texto} q={texto} /></span>
						</button>
					))}
				</div>
			) : null}
		</div>
	);
};

/** ~140 letras del texto alrededor de la primera palabra buscada (como el pedacito del servidor). */
export const pedacitoDe = (body: string | null | undefined, q: string): string => {
	const t = String(body || "").replace(/\s+/g, " ").trim();
	const w = sinAcentos(q.trim().split(/\s+/)[0] || "");
	const pos = w ? sinAcentosMismoLargo(t).indexOf(w) : -1;
	return pos < 40 ? t.slice(0, 140) : "…" + t.slice(pos - 30, pos + 110);
};
